import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

// Redirect destination based on role after login
const getRoleRedirect = (profile) => {
    if (!profile) return '/login';
    if (profile.is_super_admin || profile.role === 'admin') return '/admin/overview';
    if (profile.role === 'manager') return '/manager/overview';
    return '/app/dashboard';
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    // Impersonation state: { userId, userProfile } or null
    const [impersonating, setImpersonating] = useState(null);
    const navigate = useNavigate();

    // ── Mutex refs (persistent across renders — not recreated on each render)
    // Prevents multiple concurrent fetchProfile chains caused by:
    // - React StrictMode double-mount
    // - Multiple onAuthStateChange events (_emitInitialSession + _recoverAndRefresh)
    const fetchingRef = useRef(false);
    const lastUserIdRef = useRef(null);
    const retryTimerRef = useRef(null);

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setUser(session?.user ?? null);
            if (session?.user) {
                fetchProfile(session.user.id);
            } else {
                setLoading(false);
            }
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
            // ── PASSWORD_RECOVERY: intercept before any redirect ─────────────
            // Supabase fires this event when the user arrives via a password-reset
            // magic link. We must navigate to /update-password and stop here —
            // do NOT call fetchProfile (which would redirect them to the dashboard).
            if (_event === 'PASSWORD_RECOVERY') {
                setUser(session?.user ?? null);
                setLoading(false);
                navigate('/update-password', { replace: true });
                return;
            }

            setUser(session?.user ?? null);
            if (session?.user) {
                // ── Check for a deferred invite (email-confirmation flow) ────────
                // When a user registers via invite but email confirmation is required,
                // Register.jsx saves the token + profile to localStorage.
                // We pick it up here on the FIRST authenticated session event.
                const pendingRaw = localStorage.getItem('onet_pending_invite');
                if (pendingRaw) {
                    localStorage.removeItem('onet_pending_invite');
                    try {
                        const pending = JSON.parse(pendingRaw);
                        const { token, role: pendingRole, ...profileFields } = pending;
                        // 1. Write full profile (bypasses trigger reliability issues)
                        await supabase.from('users').upsert(
                            { id: session.user.id, ...profileFields },
                            { onConflict: 'id' }
                        );
                        // 2. Link to org via accept_invitation
                        const { data: inv } = await supabase.rpc('accept_invitation', {
                            token_input: token,
                            name_input: profileFields.name || 'Membro',
                        });
                        if (inv?.success) {
                            sessionStorage.setItem('onet_accepted_invite', '1');
                            console.info('[invite] Deferred invite accepted successfully.');
                        } else {
                            console.warn('[invite] Deferred accept_invitation failed:', inv?.error);
                        }
                    } catch (e) {
                        console.warn('[invite] Error processing deferred invite:', e);
                    }
                }
                // ────────────────────────────────────────────────────────────────
                fetchProfile(session.user.id);
            } else {
                setProfile(null);
                setTeams([]);
                setLoading(false);
            }
        });

        return () => {
            subscription.unsubscribe();
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        };
    }, []);

    const fetchProfile = async (userId, retryCount = 0) => {
        // ── Mutex guard: only one chain per user at a time
        if (retryCount === 0) {
            if (fetchingRef.current && lastUserIdRef.current === userId) return;
            fetchingRef.current = true;
            lastUserIdRef.current = userId;
            // Cancel any scheduled retry from a previous chain
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        }

        // On public/auth pages the profile legitimately doesn't exist (not logged in) —
        // skip retries entirely to avoid the exhaustion → redirect loop.
        const publicPaths = ['/login', '/register', '/onboarding', '/reset-password', '/forgot-password'];
        const onAuthPage = publicPaths.some(p => window.location.pathname.startsWith(p));

        try {
            const { data, error } = await supabase
                .from('users')
                .select('*, organizations(*)')
                .eq('id', userId)
                .maybeSingle(); // returns null (not 406) when no row found

            if (error) throw error;

            // No profile row yet — user is freshly registered (pre-onboarding)
            if (!data) {
                if (onAuthPage) {
                    console.info('No profile yet (onboarding) — standing by.');
                    fetchingRef.current = false;
                    setLoading(false);
                    return;
                }
                // Not on auth page — retry with backoff
                const MAX_RETRIES = 5;
                if (retryCount < MAX_RETRIES) {
                    const delay = Math.min(1000 * Math.pow(2, retryCount), 8000);
                    console.warn(`Profile not found, retry ${retryCount + 1}/${MAX_RETRIES} in ${delay / 1000}s…`);
                    retryTimerRef.current = setTimeout(() => fetchProfile(userId, retryCount + 1), delay);
                    return;
                }
                console.warn('Profile not found after retries — redirecting to onboarding.');
                fetchingRef.current = false;
                setProfile(null);
                setLoading(false);
                navigate('/onboarding', { replace: true });
                return;
            }

            // Se l'utente ha appena accettato un invito, aspetta che organization_id
            // sia presente nel profilo (il trigger handle_new_user crea con role='agent';
            // accept_invitation deve ancora settare org_id — finestra di race condition)
            const acceptedInvite = sessionStorage.getItem('onet_accepted_invite') === '1';
            if (acceptedInvite && (!data.organization_id || data.role === 'member')) {
                const MAX_RETRIES = 6;
                if (retryCount < MAX_RETRIES) {
                    const delay = Math.min(800 * Math.pow(1.5, retryCount), 8000);
                    console.warn(`Invited user: waiting for org+role, retry ${retryCount + 1}/${MAX_RETRIES} (org=${data.organization_id}, role=${data.role})…`);
                    retryTimerRef.current = setTimeout(() => fetchProfile(userId, retryCount + 1), delay);
                    return;
                }
                // Dopo 6 retry: vai comunque alla dashboard (non a onboarding)
                console.warn('Invited user: org/role not updated after retries — proceeding anyway.');
            }

            // Pulisce il flag solo quando org_id è presente (accept_invitation ha completato)
            if (data.organization_id && data.role !== 'member') {
                sessionStorage.removeItem('onet_accepted_invite');
            }

            if (data?.is_active === false) {
                console.warn('User deactivated. Signing out.');
                await supabase.auth.signOut();
                setUser(null);
                setProfile(null);
                fetchingRef.current = false;
                setLoading(false);
                return;
            }

            setProfile(data);
            if (data?.organization_id) {
                await fetchTeams(data.organization_id);
            }
            fetchingRef.current = false;

            // Navigate to role-appropriate dashboard after login
            // Only if currently on a public/auth page.
            // NOTE: /update-password is intentionally excluded — the user must stay
            // on that page to complete the password change (PASSWORD_RECOVERY flow).
            const publicPaths2 = ['/login', '/register', '/forgot-password'];
            const onAuthPage2 = publicPaths2.some(p => window.location.pathname.startsWith(p));
            if (onAuthPage2 && retryCount === 0) {
                navigate(getRoleRedirect(data), { replace: true });
            }

        } catch (error) {
            const isNotFound = error.code === 'PGRST116' ||
                (error.message && error.message.includes('JSON object requested'));

            if (isNotFound) {
                if (onAuthPage) {
                    // User is mid-onboarding — totally expected, no retry needed
                    console.info('No profile yet (onboarding) — standing by.');
                    fetchingRef.current = false;
                    setLoading(false);
                    return;
                }

                const MAX_RETRIES = 5;
                if (retryCount < MAX_RETRIES) {
                    // Exponential backoff: 1s, 2s, 4s, 8s, 16s
                    const delay = Math.min(1000 * Math.pow(2, retryCount), 16000);
                    console.warn(`Profile not found, retry ${retryCount + 1}/${MAX_RETRIES} in ${delay / 1000}s…`);
                    retryTimerRef.current = setTimeout(() => fetchProfile(userId, retryCount + 1), delay);
                    return; // mutex stays locked — chain continues
                }

                // Exhausted retries → redirect
                console.warn('Profile not found after retries — redirecting to onboarding.');
                fetchingRef.current = false;
                setProfile(null);
                setLoading(false);
                navigate('/onboarding', { replace: true }); // Bug #10: SPA navigation instead of hard refresh
                return;
            }

            console.error('Error fetching profile:', error);
            fetchingRef.current = false;
        } finally {
            setLoading(false);
        }
    };

    // Call this after Onboarding creates the org/profile to refresh context data
    const refreshProfile = async (userId) => {
        fetchingRef.current = false; // release mutex so a new chain can start
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        await fetchProfile(userId || user?.id);
    };

    const fetchTeams = async (organizationId) => {
        try {
            const { data, error } = await supabase
                .from('teams')
                .select('id, name')
                .eq('organization_id', organizationId);

            if (!error && data) setTeams(data);
        } catch (error) {
            console.error('Error fetching teams:', error);
        }
    };

    const signIn = async (email, password) => {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        return { data, error };
    };

    const signUp = async (email, password, metadata = {}) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: { data: metadata }, // passes name, full_name etc. to auth.users.raw_user_meta_data
        });
        return { data, error };
    };

    // Login automatico all'account demo — nessuna credenziale richiesta all'utente.
    // Prerequisiti DB:
    //   1. ALTER TABLE public.users DROP CONSTRAINT users_role_check;
    //      ALTER TABLE public.users ADD CONSTRAINT users_role_check CHECK (role = ANY (ARRAY['admin','manager','agency','agent','soloAgent','demo']));
    //   2. INSERT INTO public.users (id, email, role, name, first_name, last_name, is_active)
    //      VALUES ('<UUID_DA_AUTH>', 'demo@onet-italia.it', 'demo', 'Account Demo', 'Account', 'Demo', true);
    const DEMO_EMAIL = 'demo@onet-italia.it';
    const DEMO_PASSWORD = 'OnetDemo2026!';
    const signInAsDemo = async () => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email: DEMO_EMAIL,
            password: DEMO_PASSWORD,
        });
        return { data, error };
    };

    // ── Impersonation ────────────────────────────────────────────────────────
    // Start impersonating a user: load their profile + org, redirect to /app
    const startImpersonation = useCallback(async (targetUserId) => {
        if (!profile?.is_super_admin && profile?.role !== 'admin') {
            console.error('Unauthorized: only admins can impersonate');
            return { error: 'Unauthorized' };
        }
        try {
            const { data: targetProfile, error } = await supabase
                .from('users')
                .select('*, organizations(*)')
                .eq('id', targetUserId)
                .single();

            if (error) throw error;

            // Log the impersonation start
            await supabase.rpc('log_activity', {
                p_action_type: 'impersonation_start',
                p_entity_type: 'user',
                p_entity_id: targetUserId,
                p_metadata: {
                    impersonated_email: targetProfile.email,
                    impersonated_role: targetProfile.role,
                    admin_email: profile.email,
                },
            });

            setImpersonating({ userId: targetUserId, userProfile: targetProfile });
            // Naviga alla console corretta in base al ruolo dell'utente impersonato
            const targetRole = targetProfile.role;
            if (targetRole === 'manager') {
                navigate('/manager/overview', { replace: true });
            } else {
                navigate('/app/dashboard', { replace: true });
            }
            return { error: null };
        } catch (err) {
            console.error('Error starting impersonation:', err);
            return { error: err };
        }
    }, [profile, navigate]);

    // Stop impersonation: restore admin view
    const stopImpersonation = useCallback(async () => {
        if (!impersonating) return;
        try {
            await supabase.rpc('log_activity', {
                p_action_type: 'impersonation_end',
                p_entity_type: 'user',
                p_entity_id: impersonating.userId,
                p_metadata: { impersonated_email: impersonating.userProfile.email },
            });
        } catch (err) {
            console.warn('Error logging impersonation end:', err);
        }
        setImpersonating(null);
        navigate('/admin/overview', { replace: true });
    }, [impersonating, navigate]);

    const signOut = async () => {
        const { error } = await supabase.auth.signOut();
        if (!error) {
            setUser(null);
            setProfile(null);
            setTeams([]);
            setImpersonating(null);
        }
        return { error };
    };

    // When impersonating, expose the impersonated user's profile to the CRM
    const effectiveProfile = impersonating ? impersonating.userProfile : profile;
    const effectiveOrganization = effectiveProfile?.organizations;

    const organization = effectiveOrganization;
    const orgType = organization?.settings?.type;
    const isAgent = effectiveProfile?.role === 'agent' || effectiveProfile?.role === 'soloAgent' || orgType === 'agent';
    const isFreelance = orgType === 'freelance';
    const isSoloMode = isAgent || isFreelance;
    const hasTeams = teams && teams.length > 0;
    const isEnterprise = !isSoloMode && hasTeams;
    const isAgency = !isSoloMode && !hasTeams;
    const isDemoAccount = effectiveProfile?.role === 'demo';

    // Role-based flags (always based on REAL profile, not impersonated)
    const isSuperAdmin = profile?.is_super_admin === true;
    const isAdmin = isSuperAdmin || profile?.role === 'admin';
    const isManager = profile?.role === 'manager';

    const value = {
        user,
        profile,          // real admin/manager/agent profile
        effectiveProfile, // impersonated profile (or real if not impersonating)
        loading,
        signIn,
        signUp,
        signOut,
        signInAsDemo,
        refreshProfile,
        organizationId: effectiveProfile?.organization_id || null,
        organization,
        teams,
        isAgent,
        isFreelance,
        isSoloMode,
        isAgency,
        isEnterprise,
        hasTeams,
        isDemoAccount,
        // Admin/Manager role flags (always from real profile)
        isSuperAdmin,
        isAdmin,
        isManager,
        // Impersonation
        impersonating,
        startImpersonation,
        stopImpersonation,
        getRoleRedirect,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
